import { Auth, type MyOrganization, type SessionState } from '@dodilio/ui-auth-lib';
import { tabStore } from './tabStore';
import { mockClientInvitations } from './api/clientInvite.mock';

/*
 * Signup's view of the session, on ui-auth-lib (its CONTRACT.md): the
 * library owns tokens, storage, Keycloak and the organization list; signup
 * only decides what its pages do with the answers.
 */
export const auth: Auth = new Auth({
    api_url: import.meta.env.VITE_API_URL,
    kc_url: import.meta.env.VITE_KC_URL,
    mock: import.meta.env.VITE_USE_MOCK,
});

/*
 * A session stored by an earlier signup in this browser may be someone
 * else's (a shared device): only one this tab's signup started is trusted as
 * stored. Anything else is asked of Keycloak, which knows who is signed in now.
 */
const OWN_SESSION = 'dodil_signup_session';

/** The session this tab's signup just started (after the code, or Keycloak's callback). */
export function ownSession(): void {
    tabStore.set(OWN_SESSION, '1');
}

/** This tab's signup has a session already (the account exists). */
export const hasOwnSession = () => !!tabStore.get(OWN_SESSION);

/** Signup is over: nothing of it is trusted again. */
export function endSignupSession(): void {
    tabStore.remove(OWN_SESSION);
}

/**
 * Who is signed in now, without a redirect. Signup never needs an
 * organization in the token (it asks iam which ones a person has).
 * 'unknown' when Keycloak didn't answer: a page says "try again", never
 * "you have no account".
 */
export async function restoreSession(timeoutMs?: number): Promise<SessionState> {
    // A sign-in this tab asked for, coming back, is this signup's own; a session merely found is not.
    const ours = /[#&?]code=/.test(window.location.hash + window.location.search);
    // This tab's own signup session is the new account, and stays it, even while Keycloak still
    // holds someone else's (keepOwn); any other session follows Keycloak's.
    const own = hasOwnSession();
    const state = await auth.restore({ organization: false, trustStored: own, keepOwn: own, ...(timeoutMs ? { timeoutMs } : {}) });
    if (state === 'signed-in' && ours) ownSession();
    // Signed out (here or in another app): this tab's signup has no session any more.
    if (state === 'signed-out') endSignupSession();
    return state;
}

/** Sign in through Keycloak, back to `returnTo`. Never creates an account. */
export const signIn = (returnTo: string, loginHint?: string) => auth.signIn({ redirect: returnTo, ...(loginHint ? { loginHint } : {}) });

/**
 * Sign in to finish signing up: the org step decides from there (an account
 * with an organization goes on to where it was going; one without makes it).
 */
export const signInToFinish = (email?: string) => signIn(`${window.location.origin}/signup/org`, email);

/** Google sign-up: the only Google login that may create an account. */
export const signUpWithGoogle = (returnTo: string) => auth.signUpWithGoogle(returnTo);

/**
 * Sign out, and start signup afresh: nothing of this tab's signup is kept
 * (its session mark, who it was confirmed as), and Keycloak sends the person
 * back to "Create an account", not to a sign-in.
 */
export const signOut = (backTo = `${window.location.origin}/`) => {
    endSignupSession();
    tabStore.remove('dodil_continue_as');
    return auth.signOut({ redirect: backTo });
};

/**
 * The organizations iam lists for the signed-in person. Throws when iam
 * doesn't answer with a list: never read an error as "none".
 */
export const myOrganizations = (): Promise<MyOrganization[]> =>
    mockClientInvitations ? mockClientInvitations.orgs() : auth.myOrganizations();
